import { ensureCoreEventsInitialized } from '@/hooks/use-core-events';
import type { TrackingEvent } from '@/tracking/tracking-manager';
import {
    EVENT_TYPE,
    getGameElementData,
    getSnowplowEvent,
    SNOWPLOW_CONTEXT_MAPPING,
    type SnowplowSchemaKey
} from '@/tracking/snowplow/utils';

type DomTrackingFields = {
    gamePosition?: string | null;
    zonePosition?: string | null;
    zoneType?: string | null;
    zoneLayout?: string | null;
    gameName?: string | null;
    zoneName?: string | null;
    module?: string | null;
} | null;

export type ZoneInteractionContextData = {
    action: string | null;
    zoneType: string | null;
    zoneName: string | null;
    pageName: string | null;
    zoneId: string | null;
    zonePosition: number | null;
    zoneLayout: string | null;
    ctaLabel: string | null;
};

export type GameElementSnowplowEventParams = {
    sendEvent: (events: TrackingEvent[]) => void;
    eventType: (typeof EVENT_TYPE)[keyof typeof EVENT_TYPE];
    eventProps: Record<string, unknown>;
    trackingData: DomTrackingFields;
    gameUid?: string | null;
    gameName?: string | null;
    zoneName: string;
    zoneInteractionContext?: ZoneInteractionContextData;
};

/**
 * Sends a Snowplow event with a `game_element` context and an optional
 * `zone_interaction` context entity.
 */
export const sendGameElementSnowplowEvent = async ({
    sendEvent,
    eventType,
    eventProps,
    trackingData,
    gameUid,
    gameName,
    zoneName,
    zoneInteractionContext
}: GameElementSnowplowEventParams): Promise<void> => {
    try {
        await ensureCoreEventsInitialized();
    } catch {
        return;
    }

    const pos1Based = (Number(trackingData?.gamePosition) || 0) + 1;
    const gameElement = getGameElementData({
        zoneName,
        zonePosition: trackingData?.zonePosition ?? null,
        zoneType: trackingData?.zoneType ?? null,
        zoneLayout: trackingData?.zoneLayout ?? null,
        position: pos1Based,
        gameId: gameUid ?? null,
        gameName: gameName ?? null,
        onScreen: true,
        internalCode: null,
        triggerGameName: null,
        rankOrder: null,
        spsRank: null,
        spsSpins: null,
        modelType: null
    });

    const contexts: Array<{ contextType: string; contextProps: Record<string, unknown> }> = [
        {
            contextType: SNOWPLOW_CONTEXT_MAPPING.GAME_IMPRESSION,
            contextProps: gameElement
        }
    ];

    if (zoneInteractionContext) {
        const filteredContext: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(zoneInteractionContext)) {
            if (value !== null && value !== undefined) {
                filteredContext[key] = value;
            }
        }
        contexts.push({
            contextType: SNOWPLOW_CONTEXT_MAPPING.ZONE_INTERACTION,
            contextProps: filteredContext
        });
    }

    const snowplowEventData = getSnowplowEvent(eventType as SnowplowSchemaKey, eventProps, contexts);

    sendEvent([{ destination: 'snowplow', eventProps: snowplowEventData }]);
};
